import React, { useState, useLayoutEffect, useRef } from 'react';
import searchingImg from '../assets/NewHome/searching.png';
import mailImg from '../assets/NewHome/Mail.png';
import successImg from '../assets/NewHome/success.png';
import handImg from '../assets/NewHome/hands.png';

function Card({
  title,
  number,
  desc,
  bg,
  border,
  tall,
  noIllustration,
  image,
  imagePosition = 'bottom',
  imageWrapperClassName,
  imageClassName,
  imageStyle,
  contentClassName,
}) {
  const cardRef = React.useRef(null);
  const [cursorPos, setCursorPos] = React.useState({ x: 50, y: 50 });
  const [isHovering, setIsHovering] = React.useState(false);

  const handleMouseMove = (event) => {
    const card = cardRef.current;
    if (!card) return;

    const rect = card.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    const midX = rect.width / 2;
    const midY = rect.height / 2;

    // Normalize between -1 and 1 for tilt
    const rotateY = ((x - midX) / midX) * 10; // left/right
    const rotateX = ((y - midY) / midY) * -10; // up/down (invert for natural feel)

    // Store cursor position as percentage for the blue glow
    const percentX = (x / rect.width) * 100;
    const percentY = (y / rect.height) * 100;
    setCursorPos({ x: percentX, y: percentY });
    setIsHovering(true);

    card.style.transform = `perspective(900px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateY(-6px)`;
  };

  const handleMouseLeave = () => {
    const card = cardRef.current;
    if (!card) return;

    card.style.transform = 'perspective(900px) rotateX(0deg) rotateY(0deg) translateY(0)';
    setIsHovering(false);
  };

  return (
    <div
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className={`${bg} ${border || ''} rounded-[30px] relative p-4 sm:p-5 md:p-6 py-6 sm:py-7 md:py-8 flex ${imagePosition === 'left' || imagePosition === 'right' ? 'flex-col md:flex-row gap-4 md:gap-6' : 'flex-col'} gap-3 sm:gap-4 h-full ${tall ? "min-h-[200px] sm:min-h-[220px] md:min-h-[240px]" : "min-h-[160px] sm:min-h-[180px]"} transition-transform duration-300 ease-out will-change-transform cursor-pointer`}
      style={{
        transform: 'perspective(900px)',
      }}
    >
      {/* Blue glow that follows cursor */}
      <div
        className="pointer-events-none absolute inset-0 z-0 transition-opacity duration-200"
        style={{
          opacity: isHovering ? 1 : 0,
          background: `radial-gradient(circle at ${cursorPos.x}% ${cursorPos.y}%, rgba(59,130,246,0.25), transparent 40%)`,
        }}
      />
      {/* Number badge - top right, slightly overlapping */}
      <span className="absolute top-2 sm:top-3 right-2 sm:right-3 h-9 w-9 sm:h-10 sm:w-10 md:h-11 md:w-11 flex items-center justify-center bg-white border border-gray-300 rounded-full font-semibold text-gray-800 text-lg sm:text-xl z-10" style={{ fontFamily: 'Bricolage Grotesque, sans-serif' }}>
        {number}
      </span>
      
      {/* Image on left side */}
      {image && imagePosition === 'left' && (
        <div
          className={`hidden md:flex flex-shrink-0 items-center ${
            imageWrapperClassName || 'w-1/3'
          }`}
        >
          <img 
            src={image} 
            alt={title} 
            className={imageClassName || 'w-[60%] h-auto absolute object-contain'}
            style={{
              left: '-15%',
              bottom: '0',
              ...(imageStyle || {}),
            }}
          />
        </div>
      )}

      {/* Content section */}
      <div
        className={`flex-1 flex flex-col gap-3 sm:gap-6 ${
          contentClassName
            ? contentClassName
            : imagePosition === 'left'
            ? 'md:ml-4 lg:ml-4'
            : imagePosition === 'right'
            ? 'md:mr-0 lg:mr-0 md:max-w-[80%]'
            : ''
        }`}
      >
        {/* Title */}
        <h3 className="text-xl sm:text-2xl md:text-2xl lg:text-3xl font-extralight text-gray-800 pr-8 sm:pr-10 md:pr-8" style={{ fontFamily: 'Bricolage Grotesque, sans-serif' }}>
          {title}
        </h3>

        {/* Description */}
        <p className="text-base text-gray-700 leading-6" style={{ fontFamily: 'Bricolage Grotesque, sans-serif' }}>
          <span className="text-gray-700 font-extralight" style={{ fontFamily: 'Bricolage Grotesque, sans-serif' }}>
            {desc}
          </span>
        </p>
      </div>

      {/* Image on right side */}
      {image && imagePosition === 'right' && (
        <div
          className={`hidden md:flex flex-shrink-0 items-center justify-end ${
            imageWrapperClassName || 'w-[28%]'
          }`}
        >
          <img 
            src={image} 
            alt={title} 
            className={imageClassName || 'w-[45%] h-auto absolute object-contain'}
            style={{
              right: '2%',
              bottom: '0%',
              ...(imageStyle || {}),
            }}
          />
        </div>
      )}

      {/* Image - displayed at bottom when provided */}
      {image && imagePosition === 'bottom' && (
        <div className="flex w-full items-end justify-center mt-2 sm:mt-3 md:mt-4">
          <img 
            src={image} 
            alt={title} 
            className="w-[70%] transform -translate-x-1/2 h-auto absolute object-contain"
            style={{
              right: '0',
              bottom: '0',
            }}
          />
        </div>
      )}
    </div>
  );
}
const AccordionItem = ({ item, isOpen, onToggle, index }) => {
  const contentRef = useRef(null);
  const [contentHeight, setContentHeight] = useState(0);

  useLayoutEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [isOpen]);

  return (
    <div
      className="overflow-hidden"
      style={{
        backgroundColor: item.bgColor,
        fontFamily: "'Bricolage Grotesque', sans-serif",
        borderRadius: '40px',
        transition: 'background-color 0.3s ease-out',
        willChange: 'border-radius'
      }}
    >
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between pl-6 px-2 sm:px-6 py-2 text-left"
      >
        <h3 className="text-lg font-extralight text-gray-800 pr-4 flex-1">
          {item.title}
        </h3>
        <div className="flex-shrink-0 w-16 h-16 sm:w-9 sm:h-9 rounded-full bg-white border border-gray-300 flex items-center justify-center transition-transform duration-300"
          style={{
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)'
          }}
        >
          <svg 
            className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>
      
      <div 
        className="overflow-hidden"
        ref={contentRef}
        style={{
          maxHeight: isOpen ? `${contentHeight}px` : '0',
          opacity: isOpen ? 1 : 0,
          transition: 'max-height 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          willChange: 'max-height, opacity'
        }}
      >
        <div className="px-5 sm:px-6 pb-4 sm:pb-5">
          <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
            {item.description}
          </p>
        </div>
      </div>
    </div>
  );
};

const HowItWorksFlowchart = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const accordionItems = [
    {
      title: 'Setting up Profile',
      description:
        'WhiteOwl automatically crawls the web and trains itself to build knowledge and context about your company and its offerings.',
      bgColor: '#F8FAFF',
    },
    {
      title: 'Identifying Triggers',
      description:
        'Once the understanding is clear, it suggests triggers to bring highly relevant leads to you.',
      bgColor: '#E8F0FF',
    },
    {
      title: 'Generating Leads and Account Intelligence',
      description:
        'By keeping track of your focused accounts, companies and markets, you get high-value alerts on funding, leadership changes, strategic shifts, and M&A.',
      bgColor: '#D8D6FF',
    },
    {
      title: 'Creating Opportunities',
      description:
        'Once you have a signal, add it as an opportunity and start research to qualify. Prepare AI-generated pre-meeting drafts, discovery questions, and more.',
      bgColor: '#bed0f190',
    },
    {
      title: 'Researching',
      description:
        'Start an in-depth research on industry benchmarks, market trends, and competitors. Develop compelling storylines, build, and test hypotheses.',
      bgColor: '#B9D1FF',
    },
    {
      title: 'Sending Proposal',
      description:
        'Bring in all the information, research insights, client inputs, and past projects and let AI help you create a proposal draft. Use the built-in AI copilot to iterate and send out winning proposals faster.',
      bgColor: '#F4F7FF',
    },
  ];

  const toggleAccordion = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="max-sm:px-4 px-10 lg:px-10 xl:px-12 mt-20">
      <h1 className="max-w-7xl lg:max-w-[1400px] xl:max-w-[1500px] mx-auto text-xl my-12 text-center font-bold text-gray-900 leading-tight" style={{ fontFamily: 'Bricolage Grotesque, sans-serif' }}>
        How It Works ?
      </h1>
      {/* Mobile Accordion View */}
      <div className="md:hidden">        
        <div className="flex flex-col gap-3 sm:gap-4 max-w-md mx-auto">
          {accordionItems.map((item, index) => (
            <AccordionItem
              key={index}
              item={item}
              isOpen={openIndex === index}
              onToggle={() => toggleAccordion(index)}
              index={index}
            />
          ))}
        </div>
      </div>

      {/* Desktop Grid View */}
      <div className="hidden md:grid max-w-4xl lg:max-w-5xl xl:max-w-5xl mx-auto grid-cols-1 md:grid-cols-3 gap-2 sm:gap-2 md:gap-2">

        {/* Card 1 */}
        <Card 
          title="Setting up Profile"
          number={1}
          desc="WhiteOwl automatically crawls the web and trains itself to build knowledge and context about your company and its offerings."
          bg="bg-[#F8FAFF]"
          border="border border-gray-200"
          noIllustration={true}
        />

        {/* Card 2 */}
        <Card 
          title="Identifying Triggers"
          number={2}
          desc="Once the understanding is clear, it suggests triggers to bring highly relevant leads to you."
          bg="bg-[#E8F0FF]"
          noIllustration={true}
        />

        {/* Card 3 - TALL CARD */}
        <div className="row-span-2">
          <Card 
            title="Generating Leads and Account Intelligence"
            number={3}
            desc="By keeping track of your focused accounts, companies and markets, you get high-value alerts on funding, leadership changes, strategic shifts, and M&A."
            bg="bg-[#D8D6FF]"
            tall
            image={successImg}
            imagePosition="bottom"
          />
        </div>

        <div className="col-span-2">
          <Card 
            title="Creating opportunities"
            number={4}
            desc="Once you have a signal, add it as an opportunity and start research to qualify. Prepare AI-generated pre-meeting drafts, discovery questions, and more."
            bg="bg-[#bed0f190]"
            image={handImg}
            imagePosition="right"
            imageWrapperClassName="w-[30%]"
            imageClassName="w-[25%] h-auto absolute object-contain"
            imageStyle={{ right: '10%', bottom: '10%' }}
            contentClassName="md:mr-16 lg:mr-20 md:max-w-[70%]"
          />
        </div>
        {/* Two cards in same row 50%-50% */}
        <div className="col-span-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Left card - Researching */}
            <Card 
              title="Researching"
              number={5}
              desc="Start an in-depth research on industry benchmarks, market trends, and competitors. Develop compelling storylines, build, and test hypotheses."
              bg="bg-[#B9D1FF]"
              tall
              image={searchingImg}
              imagePosition="left"
              imageWrapperClassName="w-[40%]"
              imageClassName="w-[60%] h-auto absolute object-contain"
              imageStyle={{ left: '-10%', bottom: '0%' }}
          />
            {/* Right card - Sending Proposal */}
            <Card 
              title="Sending Proposal"
              number={6}
              desc="Bring in all the information, research insights, client inputs, and past projects and let AI help you create a proposal draft. Use the built-in AI copilot to iterate and send out winning proposals faster."
              bg="bg-[#F4F7FF]"
              image={mailImg}
              imagePosition="right"
              imageWrapperClassName="w-[34%]"
              imageClassName="w-[50%] h-auto absolute object-contain"
              imageStyle={{ right: '3%', bottom: '0%' }}
              contentClassName="md:mr-0 lg:mr-0 md:max-w-[65%]"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default HowItWorksFlowchart;

